import { Box, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { EcoEfficiencyDashboardContext } from "../../../../../contexts/EcoEfficiencyDashboardContext";
import { common, reduce } from "../../../../../i18n";
import CustomEcoEfficiencyChart from "../economicEfficiency/CustomEcoEfficiencyChart";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { MakeOptional } from "@mui/x-charts/models/helpers";
import { BarSeriesType } from "@mui/x-charts";
import { GridColDef, GridColumnGroupingModel } from "@mui/x-data-grid";
import { EcoEfficiencyByComponentDataGrid } from "../eco-efficiency-by-component/ecoEfficiencyByComponent.type";
import NumberFormatterUtils from "../../../../../utils/numberFormatterUtils";
import CustomTablePagination from "../../../../../components/dashboard/CustomTablePagination";

export const EcoEfficiencyBySubOrganization = () => {
  const { t, i18n } = useTranslation([common, reduce], {
    nsMode: "fallback",
  });

  const { selectedEcoEfficiency, yearly, indicators, subOrganizations } =
    EcoEfficiencyDashboardContext();

  const [page, setPage] = useState(0);

  const rowsPerPage = 10;

  const [series, setSeries] = useState<MakeOptional<BarSeriesType, "type">[]>(
    [],
  );

  const [labels, setLabels] = useState<string[]>([]);

  const columnGroup: GridColumnGroupingModel = [
    {
      groupId: t("Components"),
      children: yearly
        ? [
            ...yearly?.components?.map((e) => {
              return { field: e.economiccomponentsRef ?? "" };
            }),
          ]
        : [],
      headerAlign: "center",
    },
    {
      groupId: t("Normalized-Eco-Efficiency"),
      children: [
        { field: `normalizationBaseYear` },
        { field: `normalizationPlan` },
        { field: `normalizationActual` },
      ],
      headerAlign: "center",
    },
  ];

  const [rows, setRows] = useState<
    EcoEfficiencyByComponentDataGrid[] | undefined
  >([]);

  const columns: GridColDef<EcoEfficiencyByComponentDataGrid>[] = [
    {
      field: "id",
      headerName: t("No."),
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return params.row._id !== "ghg-emissions" && params.row._id !== "eco-f"
          ? `${params.id}`
          : ``;
      },
    },
    {
      field: "nameEn",
      headerName: t("Sub-Organizations"),
      width: 80,
      flex: 1,
      sortable: false,
      filterable: false,
      valueFormatter: (value, row) => {
        const subOrg = subOrganizations.find((s) => s.refId === value);

        return subOrg?.name ?? value;
      },
    },
    ...(yearly
      ? yearly.components.map((c) => {
          const indicator = indicators.find((i) =>
            i.components.find((j) => j._id === c.economiccomponentsRef),
          );

          const component = indicator?.components.find(
            (i) => i._id === c.economiccomponentsRef,
          );

          return {
            field: c.economiccomponentsRef,
            headerName:
              i18n.language === "th" ? component?.nameTh : component?.nameEn,
            flex: 1,
            sortable: false,
            filterable: false,
            headerAlign: "right",
            align: "right",
            valueFormatter: (value, row) => {
              try {
                const index =
                  row.components?.findIndex(
                    (i) => i.economiccomponentsRef === c.economiccomponentsRef,
                  ) ?? 0;

                return NumberFormatterUtils.formatNumberWithComma(
                  `${row.components![index].componentValue}`,
                );
              } catch (error) {
                return 0;
              }
            },
          } as GridColDef<EcoEfficiencyByComponentDataGrid>;
        })
      : []),
    {
      field: "normalizationBaseYear",
      headerName: t("Base-Year"),
      headerAlign: "right",
      align: "right",
      minWidth: 120,
      valueFormatter: (value) => NumberFormatterUtils.numberFormat(value) ?? 0,
    },
    // {
    //   field: "normalizationPlan",
    //   headerName: t("Plan"),
    //   headerAlign: "right",
    //   align: "right",
    //   minWidth: 120,
    //   valueFormatter: (value) => NumberFormatterUtils.numberFormat(value) ?? 0,
    // },
    {
      field: "normalizationActual",
      headerName: t("Actual"),
      headerAlign: "right",
      align: "right",
      minWidth: 120,
      renderCell: (params) => {
        return (
          <Tooltip title={params.formattedValue}>
            <Box color={"var( --status-waiting-leased-in-text)"}>
              {params.formattedValue}
            </Box>
          </Tooltip>
        );
      },
    },
  ];

  const buildTable = () => {
    if (yearly) {
      const temp: EcoEfficiencyByComponentDataGrid[] = yearly.ecoEffs.map(
        (e, i) => ({
          _id: e.suborganizationRef,
          id: `${i + 1}`,
          nameEn: e.suborganizationRef,
          nameTh: e.suborganizationRef,
          normalizationActual: e.ecoEfficiencyNormalized,
          normalizationBaseYear: e.ecoEfficiencyBaseYearNormalized,
          normalizationPlan: e.ecoEfficiencyPlanNormalized,
          unit: "1",
          valueActual: 1,
          valueBaseYear: 1,
          valuePlan: 1,
          components: e.components.map((c) => c),
        }),
      );

      setRows(temp);
    }
  };

  useEffect(() => {
    if (!yearly) return;

    const start = page * rowsPerPage;

    const end = Math.min((page + 1) * rowsPerPage, yearly.ecoEffs.length);

    setSeries([
      // {
      //   data: [
      //     ...yearly.ecoEffs
      //       .slice(start, end)
      //       .map((c) => c.ecoEfficiencyPlanNormalized),
      //   ],
      //   color: "#10B981",
      //   label: `${t("Plan")}`,
      // },
      {
        data: [
          ...yearly.ecoEffs
            .slice(start, end)
            .map((c) => c.ecoEfficiencyNormalized),
        ],
        color: "#2563EB",
        label: `${t("Actual")}`,
      },
    ]);

    setLabels(
      yearly.ecoEffs.slice(start, end).map((e) => {
        const subOrg = subOrganizations.find(
          (i) => i.refId === e.suborganizationRef,
        );

        return subOrg?.name ?? "";
      }),
    );

    buildTable();
  }, [yearly, t, page]);

  return (
    <>
      <CustomEcoEfficiencyChart
        header={`${t("Eco-Efficiency-by-Sub-Organization")} (${dayjs(
          selectedEcoEfficiency!.startDate,
        )
          .locale(i18n.language)
          .format("MMM YYYY")} - ${dayjs(selectedEcoEfficiency!.endDate)
          .locale(i18n.language)
          .format("MMM YYYY")})`}
        info={`${t("Eco-Efficiency-by-Sub-Organization")}`}
        series={series}
        labels={labels}
        year={0}
        rows={rows ?? []}
        columns={columns ?? []}
        columnGroupingModel={columnGroup ?? undefined}
        pagination={
          <CustomTablePagination
            count={rows?.length ?? 0}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(newPage) => setPage(newPage)}
          />
        }
      />
    </>
  );
};
